import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Building, BuildingStatus } from '../types';
import buildingService from '../services/buildingService';
import { useAuth } from '../contexts/AuthContext';

const inputCls = 'w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all';
const labelCls = 'text-sm font-medium text-slate-500 px-1';

export const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  NEU:                     { label: 'Neu',                     color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700' },
  ANGEBOT_ERHALTEN:        { label: 'Angebot erhalten',        color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
  BEZAHLT:                 { label: 'Bezahlt',                 color: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800' },
  INSTALLATION_TERMINIERT: { label: 'Installation Terminiert', color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
  IN_BETRIEB:              { label: 'In Betrieb',              color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' },
  STOERUNG:                { label: 'Störung',                 color: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800' },
};

const Buildings: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [formData, setFormData] = useState({
    name: '', address: '', hasPv: false, active: true,
    contactName: '', contactPhone: '', contactEmail: '',
    status: 'NEU' as BuildingStatus, statusChangedAt: '',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setBuildings(await buildingService.getAll());
    } catch { toast.error('Fehler beim Laden'); } finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        address: formData.address || undefined,
        contactName: formData.contactName.trim() || undefined,
        contactPhone: formData.contactPhone.trim() || undefined,
        contactEmail: formData.contactEmail.trim() || undefined,
        statusChangedAt: formData.statusChangedAt || undefined,
      };
      if (editingBuilding) {
        await buildingService.update(editingBuilding.id, payload);
        toast.success('Gebäude aktualisiert');
      } else {
        await buildingService.create(payload);
        toast.success('Gebäude erstellt');
      }
      resetForm(); loadData();
    } catch { toast.error('Fehler'); }
  };

  const handleEdit = (b: Building) => {
    setEditingBuilding(b);
    setFormData({
      name: b.name,
      address: b.address ?? '',
      hasPv: b.hasPv,
      active: b.active,
      contactName: b.contactName ?? '',
      contactPhone: b.contactPhone ?? '',
      contactEmail: b.contactEmail ?? '',
      status: (b.status ?? 'NEU') as BuildingStatus,
      statusChangedAt: b.statusChangedAt ?? '',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', hasPv: false, active: true, contactName: '', contactPhone: '', contactEmail: '', status: 'NEU', statusChangedAt: '' });
    setEditingBuilding(null);
    setShowForm(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center p-20">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#216dab]" />
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight transition-colors">Gebäude</h1>
          <p className="text-slate-400 dark:text-slate-500 text-base font-normal transition-colors">Liegenschaften und Zähler verwalten</p>
        </div>
        {isAdmin() && (
          <button onClick={() => setShowForm(true)} className="px-6 py-3 bg-[#216dab] dark:bg-[#216dab] hover:bg-[#1a5a8f] dark:hover:bg-[#216dab] text-white rounded-2xl font-bold text-sm shadow-lg shadow-[#216dab]/25 transition-all flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
            Neues Gebäude
          </button>
        )}
      </div>

      <div className="glass-card overflow-hidden transition-all duration-500">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/5 dark:border-white/5 bg-black/2 dark:bg-white/2">
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Gebäude</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Zähler</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">PV</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Projektstatus</th>
                {isAdmin() && (
                  <th className="px-6 py-4 text-sm font-medium text-slate-400 text-right">Aktionen</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5 dark:divide-white/5">
              {buildings.map((b) => (
                <tr key={b.id} className="group hover:bg-black/2 dark:hover:bg-white/2 transition-colors cursor-pointer" onClick={() => navigate(`/buildings/${b.id}`)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-black/5 dark:bg-white/5 rounded-xl transition-colors">
                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-base font-semibold text-slate-800 dark:text-slate-200 transition-colors">{b.name}</span>
                        <span className="text-sm text-slate-500 font-normal">{b.address || 'Keine Adresse'}</span>
                        {b.contactName && (
                          <span className="text-sm text-slate-400 font-normal">{b.contactName}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2.5 py-1 rounded-md bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors">
                      {b.meterCount} Zähler
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {b.hasPv && (
                      <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-600 dark:text-amber-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 9H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {b.status && (
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex px-2.5 py-1 rounded-md border text-sm font-medium ${STATUS_CONFIG[b.status]?.color ?? ''}`}>
                          {STATUS_CONFIG[b.status]?.label ?? b.status}
                        </span>
                        {b.statusChangedAt && (
                          <span className="text-sm text-slate-400">
                            {new Date(b.statusChangedAt).toLocaleDateString('de-DE')}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  {isAdmin() && (
                    <td className="px-6 py-4 text-right">
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(b); }} className="p-2 text-slate-400 hover:text-[#216dab] dark:hover:text-[#3aa6e9] transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gebäude erstellen/bearbeiten Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm" onClick={resetForm} />
          <div className="glass-card w-full max-w-lg relative z-10 p-8 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight mb-8">
              {editingBuilding ? 'Gebäude bearbeiten' : 'Neues Gebäude'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className={labelCls}>Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputCls} />
              </div>
              <div className="space-y-2">
                <label className={labelCls}>Adresse</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className={inputCls} />
              </div>
              <div className="flex items-center gap-6 pt-1">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-10 h-6 rounded-full relative transition-colors ${formData.hasPv ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-700'}`}>
                    <input type="checkbox" className="hidden" checked={formData.hasPv} onChange={(e) => setFormData({ ...formData, hasPv: e.target.checked })} />
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${formData.hasPv ? 'left-5' : 'left-1'}`} />
                  </div>
                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">PV-Anlage</span>
                </label>
              </div>

              {/* Ansprechpartner */}
              <div className="pt-2 border-t border-black/5 dark:border-white/5">
                <p className={labelCls + ' mb-3'}>Ansprechpartner (Optional)</p>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-400 px-1">Name</label>
                    <input type="text" value={formData.contactName} onChange={(e) => setFormData({ ...formData, contactName: e.target.value })} className={inputCls} placeholder="z.B. Max Mustermann" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-slate-400 px-1">Telefon</label>
                      <input type="tel" inputMode="numeric" value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value.replace(/\D/g, '') })} className={inputCls} placeholder="030 12345678" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-slate-400 px-1">E-Mail</label>
                      <input type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} className={inputCls} placeholder="kontakt@beispiel.de" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Projektstatus */}
              <div className="pt-2 border-t border-black/5 dark:border-white/5">
                <p className={labelCls + ' mb-3'}>Projektstatus</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-400 px-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as BuildingStatus })}
                      className={inputCls}
                    >
                      {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-400 px-1">Datum</label>
                    <input
                      type="date"
                      value={formData.statusChangedAt}
                      onChange={(e) => setFormData({ ...formData, statusChangedAt: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button type="button" onClick={resetForm} className="flex-1 px-6 py-3 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-500 rounded-2xl font-bold transition-all">Abbrechen</button>
                <button type="submit" className="flex-1 px-6 py-3 bg-[#216dab] dark:bg-[#216dab] text-white rounded-2xl font-bold shadow-lg transition-all">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Buildings;
